<template>
  <div>
    <a-form
      ref="formRef"
      class="form selectors"
      :model="formState"
      :rules="rules"
      layout="vertical"
    >
      <a-form-item label="Empresa" name="entity">
        <entity-picker :value="formState.entity" @update:value="formState.entity = $event" />
      </a-form-item>
      <a-form-item label="Campaña" name="portfolio">
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event; formState.queue = null"
          :entity="formState.entity"
        />
      </a-form-item>
      <QueuePicker
        v-model:value="formState.queue"
        default-behavior
        :portfolio="formState.portfolio"
        label="Cola"
        :showTooltip="false"
        required
        :disabled="!formState.portfolio"
        :showAll="false"
      />
      <a-form-item label="Fecha" name="date" required style="width: 300px">
        <plan-range-picker
          v-model="formState.date"
          format="DD/MM/YYYY HH:mm"
          valueFormat="YYYY-MM-DD HH:mm"
          :show-time="{ format: 'HH:mm' }"
        />
      </a-form-item>
      <div class="back">
        <span v-if="total > -1" class="total">{{ total }} Registros</span>
        <div v-else></div>
        <a-tooltip placement="top">
          <template #title>
            <span v-if="isGlobalDownloading"> Tiene una descarga pendiente </span>
            <span v-else> Los datos se descargarán de acuerdo a los filtros seleccionados. </span>
          </template>
          <a-button
            type="link"
            :disabled="disabledButton || isGlobalDownloading"
            :loading="isLoading"
            @click="handleDownload"
          >
            Descargar
            <DownloadOutlined />
          </a-button>
        </a-tooltip>
        <a-button
          type="primary"
          :disabled="disabledButton || loadingSearch"
          :loading="loadingSearch"
          @click="handleSearch"
        >
          Buscar
          <SearchOutlined />
        </a-button>
      </div>
    </a-form>
    <p class="info">Solo se filtrarán tickets resueltos de mensajería entrante, considerando cada interacción registrada en el ticket.</p>
    <div style="margin-top: 10px;">
      <a-table
        v-if="typeReport == ReportTypes.AtcMessaging"
        size="middle"
        class="table-summary"
        :columns="columnsTable"
        :data-source="reportManagement?.items"
        :scroll="scrollStyle"
        :pagination="isPagination ? reportManagement?.pagination : undefined"
        @change="handleTableChange"
      >
        <!-- TOOLTIP HEADER -->
        <template #headerCell="{ column }">
          <span v-if="column.tooltipInfo">
            {{ column.title }}
            <a-tooltip :title="column.tooltipInfo">
              <InfoCircleOutlined style="margin-left: 5px" />
            </a-tooltip>
          </span>
          <span v-else>{{ column.title }}</span>
        </template>

        <template #bodyCell="{ column, record }">
          <!-- Tiempos -->
          <template
            v-if="[
              'average_waiting_time_seconds',
              'average_agent_first_answer_time',
              'average_customer_agent_response_time',
              'average_time_to_close_seconds',
            ].includes(column.dataIndex)"
          >
            {{ secondsToHHMMSS(record[column.dataIndex]) }}
          </template>

          <!-- Porcentajes -->
          <template
            v-else-if="[
              'service_level_percentage'
            ].includes(column.dataIndex)"
          >
            {{ record[column.dataIndex] }}%
          </template>

          <!-- Default -->
          <template v-else>
            {{ record[column.dataIndex] }}
          </template>
        </template>

        <!-- SUMMARY -->
        <template #summary>
          <a-table-summary fixed>
            <a-table-summary-row>
              <a-table-summary-cell
                :index="0"
                style="font-weight:600; white-space:nowrap;"
              >
                <span style="font-weight: 600;">Total</span>
              </a-table-summary-cell>

              <!-- Total de tickets -->
              <a-table-summary-cell>
                {{ totalTickets }}
              </a-table-summary-cell>

              <!-- Total de interacciones -->
              <a-table-summary-cell>
                {{ totalInteractions }}
              </a-table-summary-cell>

              <!-- TP primera asignación -->
              <a-table-summary-cell>
                {{ totalFirstAssigned }}
              </a-table-summary-cell>

              <!-- TP transferido -->
              <a-table-summary-cell>
                {{ totalTransferred }}
              </a-table-summary-cell>

              <!-- N° NS -->
              <a-table-summary-cell>
                {{ averageNS }}
              </a-table-summary-cell>

              <!-- NS % -->
              <a-table-summary-cell>
                {{ averageNSPercentage }}%
              </a-table-summary-cell>

              <!-- TP espera -->
              <a-table-summary-cell>
                {{ secondsToHHMMSS(avgWaiting) }}
              </a-table-summary-cell>

              <!-- TP primera respuesta -->
              <a-table-summary-cell>
                {{ secondsToHHMMSS(avgFirstResponse) }}
              </a-table-summary-cell>

              <!-- TP respuesta entre mensajes -->
              <a-table-summary-cell>
                {{ secondsToHHMMSS(avgBetweenMessages) }}
              </a-table-summary-cell>

              <!-- TP resolución -->
              <a-table-summary-cell>
                {{ secondsToHHMMSS(avgResolution) }}
              </a-table-summary-cell>
            </a-table-summary-row>
          </a-table-summary>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, ref, toRaw, watch } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import { downloadReportAtcMessaging, downloadReportTypifications } from "@/app/reports/services";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { columnsFixedTableLeft } from "@/app/shared/utils/columnTable";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import { ReportTypes } from "../../utils/enums";
import { SET_REPORT_MANAGEMENT } from "../../store/reports/mutations-type";

import PlanRangePicker from "@/app/shared/components/pickers/PlanRangePicker.vue";

const columnsManagement = [
  {
    title: "Nombre del usuario",
    dataIndex: "user",
    tooltipInfo: null,
    key: "user",
    width: 250,
    fixed: "left",
  },
  {
    title: "Total de tickets de mensajería",
    dataIndex: "total_tickets",
    tooltipInfo: "Es la cantidad de tickets que cuentan con mínimo una interacción de mensajería.",
    key: "total_tickets",
    width: 150,
  },
  {
    title: "Total de interacciones de chats",
    dataIndex: "total_interactions",
    tooltipInfo: "Es la cantidad de interacciones de mensajería que tiene un ticket.",
    key: "total_interactions",
    width: 160,
  },
  {
    title: "Total de tickets de primera asignación",
    dataIndex: "total_first_assigned_tickets",
    tooltipInfo: "Cantidad de ticket nuevos cuya primera asignación fue a este agente.",
    key: "total_first_assigned_tickets",
    width: 160,
  },
  {
    title: "Total de tickets transferidos",
    dataIndex: "total_transferred_tickets",
    tooltipInfo: "Cantidad de tickets transferidos a este agente, excluyendo la primera asignación del ticket.",
    key: "total_transferred_tickets",
    width: 150,
  },
  {
    title: "N° NS",
    dataIndex: "service_level_number",
    tooltipInfo: "Número de Nivel de Servicio. Indica cuántos tickets atiende el agente dentro del NS configurado en la cola.",
    key: "service_level_number",
    width: 150,
  },
  {
    title: "NS %",
    dataIndex: "service_level_percentage",
    tooltipInfo: "Porcentaje del Nivel de Servicio.",
    key: "service_level_percentage",
    width: 150,
  },
  {
    title: "TP espera",
    dataIndex: "average_waiting_time_seconds",
    key: "average_waiting_time_seconds",
    tooltipInfo: "Tiempo desde que el Ticket esta en cola de espera por primera vez, hasta que este es asignado a un agente.",
    width: 150,
  },
  {
    title: "TP primera respuesta",
    dataIndex: "average_agent_first_answer_time",
    key: "average_agent_first_answer_time",
    tooltipInfo: "Tiempo desde que el ticket es asignado por primera vez a un agente, hasta que se le de una respuesta.",
    width: 200,
  },
  {
    title: "TP respuesta entre mensajes",
    dataIndex: "average_customer_agent_response_time",
    key: "average_customer_agent_response_time",
    tooltipInfo: "Tiempo de respuesta del agente entre mensajes del cliente. Se contabiliza por agente en caso de transferencia.",
    width: 150,
  },
  {
    title: "TP resolución",
    dataIndex: "average_time_to_close_seconds",
    key: "average_time_to_close_seconds",
    tooltipInfo: "Tiempo desde que el ticket es asignado o transferido a un agente, hasta que se resuelve o finaliza. Solo cuenta el tiempo del ultimo agente asignado al ticket.",
    width: 150,
  },
];

const secondsToHHMMSS = (seconds) => {
  if (seconds === null || seconds === undefined) return "-";
  const sec = Number(seconds);
  if (isNaN(sec)) return "-";

  const h = Math.floor(sec / 3600).toString().padStart(2, "0");
  const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");

  return `${h}:${m}:${s}`;
};

const percentFormat = (value) => {
  if (value === null || value === undefined) return "-";
  return `${value}%`;
};

export default defineComponent({
  components: {
    EntityPicker,
    CampaignPicker,
    DownloadOutlined,
    SearchOutlined,
    QueuePicker,
    InfoCircleOutlined,
    PlanRangePicker,
  },
  props: {
    typeReport: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);
    const endOpen = ref(false);
    const isLoading = ref(false);
    const isPagination = ref(false);
    const formRef = ref();
    const loadingSearch = ref(false);

    const formState = reactive({
      entity: null,
      portfolio: null,
      queue: null,
      date: null,
    });
    const rules = {
      entity: [{ required: true, message: "Campo requerido" }],
      portfolio: [{ required: true, message: "Campo requerido" }],
      queue: [{ required: true, message: "Campo requerido" }],
      date: [{ required: true, message: "Campo requerido" }],
    };

    const disabledButton = computed(() => {
      console.log("formState", formState);
      for (const key in formState) {
        if (formState[key] === null || formState[key] === undefined) return true;
      }

      return false;
    });

    const reportManagement = computed(() => store.getters["getReportManagement"]);
    const total = computed(() => reportManagement.value?.pagination?.total);
    const columnsTable = computed(() => columnsManagement);

    const { scrollStyle } = useScrollResize({ headerMargin: 470 });
    const { isGlobalDownloading } = useGlobalDownloadState();

    const items = computed(() => reportManagement.value?.items || []);

    const sum = (key) =>
      items.value.reduce((acc, item) => acc + (Number(item[key]) || 0), 0);

    const avg = (key) =>
      items.value.length ? sum(key) / items.value.length : 0;

    const totalTickets = computed(() => sum("total_tickets"));
    const totalInteractions = computed(() => sum("total_interactions"));

    const totalFirstAssigned = computed(() => sum("total_first_assigned_tickets"));
    const totalTransferred = computed(() => sum("total_transferred_tickets"));

    const averageNS = computed(() => avg("service_level_number").toFixed(2));
    const averageNSPercentage = computed(() => avg("service_level_percentage").toFixed(2));

    const avgWaiting = computed(() => avg("average_waiting_time_seconds"));
    const avgFirstResponse = computed(() => avg("average_agent_first_answer_time"));
    const avgBetweenMessages = computed(() => avg("average_customer_agent_response_time"));
    const avgResolution = computed(() => avg("average_time_to_close_seconds"));

    // const onResize = () => {
    //   scrollStyle.value = { x: 1800, y: window.innerHeight - 420 };
    // };

    onMounted(() => {
      formRef.value.resetFields();
      store.commit("SET_COMMON_REPORTS", []);
      isPagination.value = false;
    });

    const handleDownload = async () => {
      try {
        isLoading.value = true;
        await downloadReportAtcMessaging({
          entity: toRaw(formState).entity,
          portfolio: toRaw(formState).portfolio,
          queue: toRaw(formState).queue,
          startValue: formState.date.at(0),
          endValue: formState.date.at(1),
          type: 30,
        });
        isLoading.value = false;
      } catch (error) {
        console.log("Error");
        message.error("Ha ocurrido un error");
      }
    };

    const handleTableChange = (pagination) => {
      store.dispatch("fetchReportAtcMessaging", {
        query: {
          entity: toRaw(formState).entity,
          portfolio: toRaw(formState).portfolio,
          queue: toRaw(formState).queue,
          startValue: formState.date.at(0),
          endValue: formState.date.at(1),
        },
        pagination: pagination,
      });
    };

    watch(user, (newValue) => {
      if (newValue.customer.type_business == 2) {
        formState.entity.value = 1;
      }
    });

    const handleSearch = async () => {
      loadingSearch.value = true;
      await store.dispatch("fetchReportAtcMessaging", {
        query: {
          entity: toRaw(formState).entity,
          portfolio: toRaw(formState).portfolio,
          queue: toRaw(formState).queue,
          startValue: formState.date.at(0),
          endValue: formState.date.at(1),
        },
        pagination: {},
      });
      isPagination.value = true;
      loadingSearch.value = false;
    };

    onMounted(() => {
      formRef.value.resetFields();
      store.commit(SET_REPORT_MANAGEMENT, null);
    });

    return {
      formRef,
      formState,
      rules,
      user,
      handleTableChange,
      dateTimeToSlashFormat,
      disabledButton,
      reportManagement,
      total,
      handleDownload,
      columnsTable,
      handleSearch,
      scrollStyle,
      isLoading,
      loadingSearch,
      isPagination,
      isGlobalDownloading,
      secondsToHHMMSS,
      totalTickets,
      totalInteractions,
      totalFirstAssigned,
      totalTransferred,
      averageNS,
      averageNSPercentage,
      avgWaiting,
      avgFirstResponse,
      avgBetweenMessages,
      avgResolution,
      ReportTypes,
    };
  },
});
</script>

<style lang="sass" scoped>
.selectors
  display: grid
  grid-template-columns: 1fr 1fr 1fr 1fr 2fr
  gap: 15px
  padding: 15px 5px 0
:deep(.ant-calendar-picker-icon)
  top: 50% !important
:deep(.ant-calendar-picker)
  width: 100%
.ant-form-item
  margin-bottom: 0
.back
  align-self: end
  text-align: right
.main-text
  font-size: 14px
  font-style: normal
  font-weight: 400
  line-height: 28px
  letter-spacing: 0em
  text-align: center
  color: $gray-7
.total
  margin-top: 2%
  color: $gray-8
.info
  margin-top: 0
  margin-left: 5px
  color: $gray-7

#table-summary tfoot th,
#table-summary tfoot td
  background: #fafafa !important

[data-theme='dark'] #table-summary tfoot th,
[data-theme='dark'] #table-summary tfoot td
  background: #1d1d1d !important
</style>
